import { ReactiveController, ReactiveControllerHost, nothing, noChange } from 'lit';
import { Directive, directive, ElementPart, PartInfo, PartType } from 'lit/directive.js';

import {
  createForm,
  Config,
  FormApi,
  formSubscriptionItems,
  FieldConfig,
  FormSubscription,
  Unsubscribe,
} from 'final-form';

export type { Config as FormConfig } from 'final-form';

interface FormSubscriptionX extends FormSubscription {
  [key: string]: boolean | undefined;
}

const allFormSubscriptionItems = formSubscriptionItems.reduce<FormSubscriptionX>(
  (acc, item) => ((acc[item] = true), acc),
  {},
);

export class FinalFormController<FormValues> implements ReactiveController {
  private __host: ReactiveControllerHost;
  private __subscription: FormSubscription = allFormSubscriptionItems;
  private __unsubscribe?: Unsubscribe;
  form: FormApi<FormValues>;

  // https://final-form.org/docs/final-form/types/Config
  constructor(host: ReactiveControllerHost, config: Config<FormValues>, subscription?: FormSubscription) {
    (this.__host = host).addController(this);
    if (subscription) {
      this.__subscription = subscription;
    }
    this.form = createForm(config);
  }

  hostConnected() {
    this.__unsubscribe = this.form.subscribe(() => {
      this.__host.requestUpdate();
    }, this.__subscription);
  }

  hostDisconnected() {
    if (this.__unsubscribe) this.__unsubscribe();
  }

  // https://final-form.org/docs/final-form/types/FieldConfig
  register = <K extends keyof FormValues>(name: K, fieldConfig?: FieldConfig<FormValues[K]>) => {
    return registerDirective(this.form, name, fieldConfig);
  };
}

class RegisterDirective extends Directive {
  private __registered = false;

  constructor(partInfo: PartInfo) {
    super(partInfo);
    if (partInfo.type !== PartType.ELEMENT) {
      throw new Error('The `register` directive must be used in the `element` attribute');
    }
  }

  update(part: ElementPart, [form, name, fieldConfig]: Parameters<this['render']>) {
    if (!this.__registered) {
      form.registerField(
        name,
        (fieldState) => {
          const { blur, change, focus, value } = fieldState;
          const el = part.element as HTMLInputElement | HTMLSelectElement;
          el.name = String(name);
          if (!this.__registered) {
            el.addEventListener('blur', () => blur());
            el.addEventListener('input', (event) =>
              change(
                el.type === 'checkbox'
                  ? (event.target as HTMLInputElement).checked
                  : (event.target as HTMLInputElement).value,
              ),
            );
            el.addEventListener('focus', () => focus());
          }
          if (el.type === 'checkbox') {
            (el as HTMLInputElement).checked = value;
          } else {
            el.value = value === undefined ? '' : value;
          }
        },
        { value: true },
        fieldConfig,
      );
      this.__registered = true;
    }

    return noChange;
  }

  // Placeholder only; update() performs the real work.
  render(_form: FormApi<any>, _name: PropertyKey, _fieldConfig?: FieldConfig<any>) {
    return nothing;
  }
}

const registerDirective = directive(RegisterDirective);
